
import React, { useEffect, useState, useRef } from 'react';
import { HashRouter as Router, Route, Routes, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import UploadModal from './components/UploadModal';
import AccountModal from './components/AccountModal';
import FlipbookViewer from './components/FlipbookViewer';
import QRModal from './components/QRModal';
import Login from './components/Login';
import BrandingSettings from './components/BrandingSettings';
import Analytics from './components/Analytics';
import QRCodes from './components/QRCodes';
import PublicLibrary from './components/PublicLibrary';
import ExportGuide from './components/ExportGuide';
import AuthGuard from './components/AuthGuard'; 
import { fetchBooklets, getBookletById, getCachedBookletList, recordReadingTime, recordShare, subscribeToAuth, logout, getBrandingSettings, isAppInDemoMode } from './services/firebase';
import { Booklet, User, AppSettings } from './types';
import { AlertTriangle, Database, LogIn } from 'lucide-react';

// Robust URL Generator
const getShareUrl = (id: string) => {
  const baseUrl = window.location.href.split('#')[0];
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${cleanBase}/#/view/${id}`;
};

// --- Helper Component for the Viewer Route ---
const ViewerRoute = ({ booklets, isPublic }: { booklets: Booklet[], isPublic: boolean }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booklet, setBooklet] = useState<Booklet | null>(null);
  const [status, setStatus] = useState<'loading' | 'found' | 'error'>('loading');
  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    const loadBooklet = async () => {
      if (!id) {
        if (isMounted) setStatus('error');
        return;
      }

      // 1. Optimistic Check: Try to find it in the props list first (Instant).
      // List items are now lightweight (no page URLs), so only use the cached
      // item if it actually carries pages — otherwise fetch the full booklet.
      const found = booklets.find(b => b.id === id);
      if (found && found.pages && found.pages.length > 0) {
        if (isMounted) {
          setBooklet(found);
          setStatus('found');
        }
        return;
      }

      // 2. Network Fetch: If not in props, fetch directly
      try {
        const fetched = await getBookletById(id, !isPublic);
        if (isMounted) {
          if (fetched) {
            setBooklet(fetched);
            setStatus('found');
          } else {
            setStatus('error');
          }
        }
      } catch (e) {
        console.error("Failed to load booklet", e);
        if (isMounted) setStatus('error');
      }
    };

    loadBooklet();

    return () => { isMounted = false; };
  }, [id, booklets]);

  // READING TIME TRACKING
  // Starts when the booklet is shown; records the session duration when the
  // reader leaves (close button, back navigation, or tab hidden/closed).
  // Records at most once per session; capped and filtered in recordReadingTime.
  useEffect(() => {
    if (status !== 'found' || !id) return;

    const start = Date.now();
    let recorded = false;

    const record = () => {
      if (recorded) return;
      recorded = true;
      recordReadingTime(id, (Date.now() - start) / 1000);
    };

    // Tab hidden or being closed — best-effort flush (covers mobile + tab close)
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') record();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', record);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', record);
      record(); // normal exit: close button / navigation
    };
  }, [status, id]);

  if (status === 'loading') {
    return (
      <div className="h-screen flex items-center justify-center bg-[#0d0d0d] text-white/30 text-sm font-serif tracking-widest uppercase animate-pulse">
        Loading Publication...
      </div>
    );
  }

  if (status === 'error' || !booklet) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#0d0d0d] text-white p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-white/20 mb-4" />
        <h2 className="font-serif text-xl text-white mb-2">Publication Not Found</h2>
        <p className="text-white/40 text-sm max-w-md mb-8">
          The booklet you are looking for has been removed, is set to draft, or is currently unavailable.
        </p>
        <div className="flex gap-4">
            <button 
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-full text-xs font-bold uppercase tracking-widest transition-colors"
            >
            Return Home
            </button>
            <button 
            onClick={() => navigate('/login')}
            className="px-6 py-2 border border-white/20 hover:bg-white/5 rounded-full text-xs font-bold uppercase tracking-widest transition-colors flex items-center gap-2"
            >
            <LogIn size={14} /> Admin Login
            </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black">
        <FlipbookViewer 
          booklet={booklet} 
          onClose={() => navigate('/')} 
          onShare={() => { setIsShareOpen(true); recordShare(booklet.id); }}
          isPublic={isPublic}
        />
      </div>
      <QRModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        url={getShareUrl(booklet.id)}
        title={booklet.title}
      />
    </>
  );
};

const DemoBanner = () => {
    if (!isAppInDemoMode()) return null;
    return (
        <div className="bg-amber-100 border-b border-amber-200 text-amber-900 px-4 py-2 text-xs flex items-center justify-center gap-2 sticky top-0 z-[60]">
            <Database size={14} />
            <span className="font-bold">LOCAL DEMO MODE:</span>
            <span>App is NOT connected to Firebase. Data is saved to this browser only. Shared links will NOT work on other devices.</span>
        </div>
    );
};

// Wrapper to use hooks inside Router
const AppContent: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  
  const [booklets, setBooklets] = useState<Booklet[]>([]);
  const [bookletsLoading, setBookletsLoading] = useState(true);
  const [appSettings, setAppSettings] = useState<AppSettings>({});
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  
  const [editingBooklet, setEditingBooklet] = useState<Booklet | null>(null);
  const dataLoadedRef = useRef(false);
  
  // Tutorial State
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Check for tutorial trigger in URL on route changes or initial load
  useEffect(() => {
    const checkTutorial = () => {
      if (window.location.hash.includes('tutorial=true')) {
        setIsTutorialActive(true);
      }
    };
    checkTutorial();
    window.addEventListener('hashchange', checkTutorial);
    return () => window.removeEventListener('hashchange', checkTutorial);
  }, []);

  useEffect(() => {
    const unsub = subscribeToAuth((u) => {
      setUser(u ? { 
        uid: u.uid, 
        email: u.email, 
        displayName: u.displayName,
        photoURL: u.photoURL,
        emailVerified: u.emailVerified
      } : null);
      setLoadingAuth(false);
    });
    return unsub;
  }, []);

  // DATA REFRESH LOGIC
  // We re-fetch when:
  // 1. Auth loads
  // 2. The URL changes to dashboard/library (This ensures when returning from Viewer -> Dashboard, we see new leads/stats)
  useEffect(() => {
    if (loadingAuth) return;

    getBrandingSettings().then(setAppSettings).catch(console.error);
    
    const isPublic = !user;

    // Instant paint: show the cached list from the last visit immediately,
    // then replace it with fresh data as soon as the fetch below completes.
    if (!dataLoadedRef.current) {
        const cached = getCachedBookletList(isPublic);
        if (cached.length > 0) {
            setBooklets(cached);
            setBookletsLoading(false);
        }
    }

    const loadData = async () => {
        const data = await fetchBooklets(undefined, isPublic);
        setBooklets(data);
        dataLoadedRef.current = true;
        setBookletsLoading(false);
    };

    if (location.pathname === '/' || location.pathname === '/library' || !dataLoadedRef.current) {
        loadData();
    }
  }, [user, loadingAuth, location.pathname]);

  const handleLogout = async () => {
    await logout();
    setUser(null);
  };

  const handleUploadClick = () => {
    setEditingBooklet(null); 
    setIsUploadOpen(true);
  };

  const handleHomeClick = () => {
    navigate('/');
    setResetKey(k => k + 1);
    setIsUploadOpen(false);
    setIsAccountOpen(false);
    setEditingBooklet(null);
    setIsTutorialActive(false);
  };

  const handleEditClick = (booklet: Booklet) => {
    setEditingBooklet(booklet);
    setIsUploadOpen(true);
  };

  const handleUploadComplete = (updatedBooklet: Booklet) => {
    setBooklets(prev => {
        const exists = prev.findIndex(b => b.id === updatedBooklet.id);
        if (exists >= 0) {
            const newList = [...prev];
            newList[exists] = updatedBooklet;
            return newList;
        }
        return [updatedBooklet, ...prev];
    });
    setIsUploadOpen(false);
    setEditingBooklet(null);
  };

  const handleDeleteComplete = (deletedId: string) => {
      setBooklets(prev => prev.filter(b => b.id !== deletedId));
      setIsUploadOpen(false);
      setEditingBooklet(null);
  };

  // Calculate publications by this user
  const userPublicationCount = user 
    ? booklets.filter(b => b.ownerId === user.uid).length 
    : 0;

  if (loadingAuth) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-white font-serif text-2xl animate-pulse text-dark">
        Esselle Retail
      </div>
    );
  }

  return (
    <>
      <DemoBanner />
      <Routes>
        <Route path="/view/:id" element={ 
           <ViewerRoute booklets={booklets} isPublic={!user} /> 
        } />

        <Route path="/login" element={!user ? <Login onLoginSuccess={setUser} logoSrc={appSettings.logoUrl} companyName={appSettings.companyName} /> : <Navigate to="/" />} />
        
        <Route path="/" element={
          user ? (
            <AuthGuard user={user}>
                <Layout 
                    user={user} 
                    onUploadClick={handleUploadClick} 
                    onLogout={handleLogout} 
                    logoSrc={appSettings.logoUrl}
                    onOpenProfile={() => setIsAccountOpen(true)}
                    onHomeClick={handleHomeClick}
                >
                <Dashboard 
                    key={resetKey}
                    booklets={booklets} 
                    isLoading={bookletsLoading}
                    onView={(id) => window.location.hash = `/view/${id}`} 
                    onEdit={handleEditClick}
                    runTutorial={isTutorialActive} // Pass tutorial state
                    onTutorialFinish={() => setIsTutorialActive(false)} // Clear state on finish
                />
                </Layout>
            </AuthGuard>
          ) : (
            <PublicLibrary 
               booklets={booklets} 
               isLoading={bookletsLoading}
               appSettings={appSettings}
               onView={(id) => window.location.hash = `/view/${id}`}
            />
          )
        } />

        <Route path="/analytics" element={
          user ? (
            <AuthGuard user={user}>
                <Layout 
                    user={user} 
                    onUploadClick={handleUploadClick} 
                    onLogout={handleLogout} 
                    logoSrc={appSettings.logoUrl}
                    onOpenProfile={() => setIsAccountOpen(true)}
                    onHomeClick={handleHomeClick}
                >
                <Analytics booklets={booklets} />
                </Layout>
            </AuthGuard>
          ) : <Navigate to="/login" />
        } />

        <Route path="/qrcodes" element={
          user ? (
            <AuthGuard user={user}>
                <Layout 
                    user={user} 
                    onUploadClick={handleUploadClick} 
                    onLogout={handleLogout} 
                    logoSrc={appSettings.logoUrl}
                    onOpenProfile={() => setIsAccountOpen(true)}
                    onHomeClick={handleHomeClick}
                >
                <QRCodes booklets={booklets} />
                </Layout>
            </AuthGuard>
          ) : <Navigate to="/login" />
        } />

        <Route path="/branding" element={
          user ? (
            <AuthGuard user={user}>
                <Layout 
                    user={user} 
                    onUploadClick={handleUploadClick} 
                    onLogout={handleLogout} 
                    logoSrc={appSettings.logoUrl}
                    onOpenProfile={() => setIsAccountOpen(true)}
                    onHomeClick={handleHomeClick}
                >
                <BrandingSettings currentSettings={appSettings} onUpdate={setAppSettings} />
                </Layout>
            </AuthGuard>
          ) : <Navigate to="/login" />
        } />

        <Route path="/export-guide" element={
          user ? (
            <AuthGuard user={user}>
                <Layout 
                    user={user} 
                    onUploadClick={handleUploadClick} 
                    onLogout={handleLogout} 
                    logoSrc={appSettings.logoUrl}
                    onOpenProfile={() => setIsAccountOpen(true)}
                    onHomeClick={handleHomeClick}
                >
                <ExportGuide />
                </Layout>
            </AuthGuard>
          ) : <Navigate to="/login" />
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {user && (
          <UploadModal 
            isOpen={isUploadOpen} 
            onClose={() => setIsUploadOpen(false)} 
            onUploadComplete={handleUploadComplete}
            onDeleteComplete={handleDeleteComplete}
            initialBooklet={editingBooklet}
            currentUser={user}
            runTutorial={isTutorialActive} // Pass tutorial state
          />
      )}

      {user && (
          <AccountModal 
            isOpen={isAccountOpen}
            onClose={() => setIsAccountOpen(false)}
            user={user}
            publicationCount={userPublicationCount}
            onProfileUpdate={setUser}
          />
      )}
    </>
  );
};

const App: React.FC = () => {
    return (
        <Router>
            <AppContent />
        </Router>
    );
}

export default App;
